import React from "react";

// A paper till receipt from Universe & Co. — the artifact you get for placing
// a manifestation order. Used on the checkout confirmation and in the order
// history. Pure presentational: pass the order's lines and labels in.

// Deterministic per-receipt barcode: hash the order id into bar widths so
// every receipt scans a little differently.
const barcodeBars = (seedText) => {
  let h = 2166136261;
  for (let i = 0; i < seedText.length; i += 1) {
    h ^= seedText.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return Array.from({ length: 32 }, (_, i) => {
    h ^= h << 13;
    h ^= h >>> 17;
    h ^= h << 5;
    return 1 + (Math.abs(h + i) % 4);
  });
};

const Dashes = () => (
  <div className="my-3 border-t border-dashed border-[#2b2418]/30" />
);

const LeaderRow = ({ left, right }) => (
  <div className="flex items-baseline gap-1">
    <span className="shrink-0">{left}</span>
    <span className="mb-1 flex-1 border-b border-dotted border-[#2b2418]/40" />
    <span className="shrink-0">{right}</span>
  </div>
);

const CosmicReceipt = ({ lines, dateLabel, orderNo, stamp = "IT IS DONE", tilt = -1.5 }) => (
  <div
    className="relative mx-auto w-full max-w-sm font-mono text-[13px] leading-relaxed text-[#2b2418]"
    style={{ transform: `rotate(${tilt}deg)` }}
  >
    <div className="bg-[#fdf6e7] px-6 pb-6 pt-6 shadow-[0_24px_60px_-18px_rgba(0,0,0,0.85)]">
      <div className="text-center">
        <div className="text-base font-bold tracking-[0.2em]">✦ UNIVERSE &amp; CO. ✦</div>
        <div className="mt-1 text-[11px] uppercase tracking-[0.25em] text-[#2b2418]/70">
          Cosmic Commissary
        </div>
        <div className="mt-1 text-[11px] italic text-[#2b2418]/60">
          everything · everywhere · eventually
        </div>
      </div>

      <Dashes />
      <div className="flex justify-between text-[11px] uppercase tracking-wider text-[#2b2418]/70">
        <span>{dateLabel}</span>
        <span>{orderNo}</span>
      </div>
      <Dashes />

      <div className="space-y-2">
        {lines.map((line, i) => (
          <div key={i}>
            <LeaderRow left={`${line.emoji} ${line.label}`} right="0.00" />
            <div className="pl-6 text-[11px] text-[#2b2418]/60">for {line.recipient}</div>
            {line.note && (
              <div className="pl-6 text-[11px] italic text-[#2b2418]/60">“{line.note}”</div>
            )}
          </div>
        ))}
      </div>

      <Dashes />
      <LeaderRow left="SUBTOTAL" right="0.00" />
      <LeaderRow left="TRUST" right="included" />
      <div className="mt-1 font-bold">
        <LeaderRow left="TOTAL" right="$0.00" />
      </div>
      <div className="mt-2 text-[11px] uppercase tracking-wider text-[#2b2418]/70">
        paid in full — method: faith
      </div>

      <Dashes />
      <div className="text-[11px] uppercase tracking-wider text-[#2b2418]/70">
        <div>delivery: divine timing</div>
        <div>tracking: just notice</div>
        <div>returns: never needed</div>
      </div>

      <div className="mt-4 flex h-10 items-end justify-center gap-[2px]" aria-hidden>
        {barcodeBars(orderNo || "universe").map((w, i) => (
          <span key={i} className="h-full bg-[#2b2418]" style={{ width: `${w}px` }} />
        ))}
      </div>
      <div className="mt-1 text-center text-[10px] tracking-[0.35em] text-[#2b2418]/70">
        {orderNo}
      </div>

      <div className="mt-4 text-center text-[11px] italic text-[#2b2418]/60">
        thank you for manifesting with us ✦
      </div>
    </div>

    {/* Torn zigzag bottom edge */}
    <div
      aria-hidden
      className="h-3 w-full"
      style={{
        background:
          "linear-gradient(-45deg, #fdf6e7 6px, transparent 0), linear-gradient(45deg, #fdf6e7 6px, transparent 0)",
        backgroundPosition: "left top",
        backgroundRepeat: "repeat-x",
        backgroundSize: "12px 12px",
      }}
    />

    {/* Ink stamp */}
    {stamp && (
      <div
        className="absolute -right-3 top-16 rotate-[-14deg] rounded border-4 border-double border-rose-700/70 px-3 py-1 text-sm font-bold uppercase tracking-[0.2em] text-rose-700/80 mix-blend-multiply"
      >
        {stamp}
      </div>
    )}
  </div>
);

export default CosmicReceipt;
